import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FolderKanban } from 'lucide-react';
import { toast } from 'react-toastify';

interface ProjectRow {
  id: string;
  title: string;
  description: string;
  requesterName: string;
  proposalCount: number;
  status: string;
  createdAt: Date;
}

const STATUS_ORDER = ['pending', 'active', 'closed', 'cancelled'];

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  active: 'En cours',
  closed: 'Clôturé',
  cancelled: 'Annulé',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
};

const AdminProjects = () => {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);

        // Fetch all projects
        const projectsSnapshot = await getDocs(collection(db, 'projects'));

        // Fetch all users for requester names
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersMap: Record<string, string> = {};
        usersSnapshot.docs.forEach(d => {
          const data = d.data();
          usersMap[d.id] = data.username || data.email || d.id;
        });

        // For each project, count proposals
        const rows: ProjectRow[] = await Promise.all(
          projectsSnapshot.docs.map(async (d) => {
            const data = d.data();

            let proposalCount = 0;
            try {
              const q = query(collection(db, 'proposals'), where('projectId', '==', d.id));
              const count = await getCountFromServer(q);
              proposalCount = count.data().count;
            } catch {
              // best-effort
            }

            return {
              id: d.id,
              title: data.name || data.title || '—',
              description: data.shortDescription || data.description || '—',
              requesterName: usersMap[data.userId] || data.userId || '—',
              proposalCount,
              status: data.status || 'pending',
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
            };
          })
        );

        setProjects(rows);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Erreur lors du chargement des projets');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const grouped = STATUS_ORDER.reduce<Record<string, ProjectRow[]>>((acc, status) => {
    acc[status] = projects
      .filter(p => p.status === status)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-8">
        <FolderKanban className="h-6 w-6 text-primary-600 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Projets</h1>
        <span className="ml-3 text-sm text-gray-500">({projects.length} projet{projects.length !== 1 ? 's' : ''})</span>
      </div>

      <div className="space-y-10">
        {STATUS_ORDER.map(status => {
          const group = grouped[status];
          return (
            <section key={status}>
              <div className="flex items-center mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STATUS_COLORS[status]}`}>
                  {STATUS_LABELS[status]}
                </span>
                <span className="ml-2 text-sm text-gray-400">{group.length} projet{group.length !== 1 ? 's' : ''}</span>
              </div>

              {group.length === 0 ? (
                <p className="text-sm text-gray-400 italic pl-1">Aucun projet dans cette catégorie.</p>
              ) : (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Demandeur</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Propositions</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {group.map(project => (
                          <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <p className="text-sm font-medium text-gray-900">{project.title}</p>
                            </td>
                            <td className="px-6 py-4 max-w-xs">
                              <p className="text-sm text-gray-600 truncate">{project.description}</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-700">{project.requesterName}</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">
                                {project.proposalCount}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[project.status] || 'bg-gray-100 text-gray-700'}`}>
                                {STATUS_LABELS[project.status] || project.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {project.createdAt.toLocaleDateString('fr-FR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default AdminProjects;
